// A short confetti burst over the composer when a recognition lands.
//
// No library: a dozen absolutely-positioned emoji on CSS transforms is the
// whole effect, and pulling a canvas dependency in for two seconds of delight
// would cost more than it is worth on every page load.
//
// Deliberately decorative — aria-hidden, pointer-events-none, and it respects
// prefers-reduced-motion by simply not rendering. Nobody should have to sit
// through an animation to find out their recognition posted; the thread
// message is the real confirmation and this is a flourish on top.
import React, { useEffect, useMemo, useState } from 'react';

const PIECES = ['🎉', '🎊', '⭐', '🏆', '✨', '👏'];
const DURATION_MS = 1600;

export const CelebrationBurst: React.FC<{ onDone?: () => void }> = ({ onDone }) => {
  const [gone, setGone] = useState(false);

  const reduced = useMemo(() => {
    try {
      return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    } catch {
      return false;
    }
  }, []);

  // Fixed per mount, so a re-render does not reshuffle mid-flight.
  const bits = useMemo(
    () =>
      Array.from({ length: 14 }, (_, i) => ({
        glyph: PIECES[i % PIECES.length],
        left: 6 + Math.random() * 88, // %
        delay: Math.random() * 220,   // ms
        drift: -40 + Math.random() * 80, // px
        spin: -120 + Math.random() * 240, // deg
        scale: 0.75 + Math.random() * 0.6,
      })),
    [],
  );

  useEffect(() => {
    if (reduced) {
      onDone?.();
      return;
    }
    const h = window.setTimeout(() => { setGone(true); onDone?.(); }, DURATION_MS);
    return () => window.clearTimeout(h);
  }, [reduced, onDone]);

  if (reduced || gone) return null;

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-0 bottom-0 z-30 h-40 overflow-hidden"
      data-testid="celebration-burst"
    >
      <style>{`
        @keyframes kp-confetti-rise {
          0%   { opacity: 0; transform: translate(0, 10px) rotate(0deg) scale(var(--s)); }
          15%  { opacity: 1; }
          100% { opacity: 0; transform: translate(var(--dx), -140px) rotate(var(--r)) scale(var(--s)); }
        }
      `}</style>
      {bits.map((b, i) => (
        <span
          key={i}
          className="absolute bottom-2 text-lg"
          style={{
            left: `${b.left}%`,
            // CSS vars keep the keyframes shared rather than one per piece.
            ['--dx' as string]: `${b.drift}px`,
            ['--r' as string]: `${b.spin}deg`,
            ['--s' as string]: `${b.scale}`,
            animation: `kp-confetti-rise ${DURATION_MS}ms ease-out ${b.delay}ms forwards`,
          }}
        >
          {b.glyph}
        </span>
      ))}
    </div>
  );
};

/**
 * The message pasted into the thread so the room sees it happened.
 *
 * Written as a short announcement rather than a dump of the form: the title
 * and value say what it was for, and the message carries the words the person
 * actually chose. The recipient is NOT @mentioned — recognition already sends
 * them their own notification, and a mention on top would be two pings for one
 * act of thanks.
 */
export function celebrationMessage(v: {
  recipientName: string;
  alsoNames?: string[];
  categoryLabel?: string;
  title?: string;
  message?: string;
}): string {
  const who = [v.recipientName, ...(v.alsoNames ?? [])].filter(Boolean);
  const names =
    who.length <= 1
      ? who[0] ?? ''
      : `${who.slice(0, -1).join(', ')} and ${who[who.length - 1]}`;

  const lines = [`🏆 Recognised ${names}${v.categoryLabel ? ` — ${v.categoryLabel}` : ''}`];
  if (v.title?.trim()) lines.push(v.title.trim());
  if (v.message?.trim() && v.message.trim() !== v.title?.trim()) lines.push(`"${v.message.trim()}"`);
  return lines.join('\n');
}
